import { createSelector } from 'reselect'
import { smallScreen } from 'variables'
import { authSelector } from './baseSelectors'
import { accessListSelector, getAccess } from './accessSelectors'
import { isLoaded } from 'react-redux-firebase'
import { currentFolderIdSelector, currentBoardIdSelector } from './selectors'
import {
  BOARDS,
  VIEWED,
  CREATED,
  NAME,
  UNFILED,
  SHARED,
  MINE,
  ARCHIVED
} from '../../constants'

export const sidebarCloseableSelector = createSelector(
  [currentBoardIdSelector],
  board => !!board
)
export const sidebarShowingSelector = createSelector(
  [
    state => state.sidebar.open,
    authSelector,
    state => state.board.size.width < smallScreen
  ],
  (open, auth, isSmallScreen) => {
    // if state is set, return that
    if (open !== undefined) return open
    // default to closed on small screens
    if (isSmallScreen) return false
    // default to closed for anon users
    if (auth.isAnonymous) return false
    return true
  }
)

export const sidebarSectionSelector = state => state.sidebar.section
export const sidebarFilterSelector = state =>
  state.sidebar.section === BOARDS
    ? state.sidebar.boardsFilter
    : state.sidebar.foldersFilter

// current location based on folder in url or parent of board in url
export const sidebarFolderSelector = createSelector(
  [currentFolderIdSelector, currentBoardIdSelector, accessListSelector],
  (folderId, boardId, accessList) => {
    if (!isLoaded(accessList)) return
    if (!accessList) return null
    const board = accessList[boardId]
    if (board && board.parent) folderId = board.parent
    if (!folderId) return null
    const access = getAccess(folderId, accessList)
    if (!access) return null
    return { id: folderId, access }
  }
)

const lower = item => (item.name || '').toLowerCase()
const dateCreated = (a, b) => (b.dateCreated || 0) - (a.dateCreated || 0)

export const sidebarItemsSelector = createSelector(
  [
    accessListSelector,
    sidebarFolderSelector,
    sidebarSectionSelector,
    sidebarFilterSelector,
    authSelector
  ],
  (accessList, folder, section, filter, user) => {
    if (!isLoaded(accessList) || !isLoaded(folder)) return
    let items = Object.values(accessList || {})

    items = items.filter(i => i.accessLevel)

    if (filter === ARCHIVED) {
      return items
        .filter(i => i.dateArchived)
        .sort((a, b) => b.dateArchived - a.dateArchived)
    } else {
      // filter archived items
      items = items.filter(item => !item.dateArchived)
    }

    if (folder) {
      return items
        .filter(i => i.parent === folder.id)
        .sort((a, b) => b.sortOrder - a.sortOrder)
    } else {
      if (user.isEmpty || user.isAnonymous) return items
      if (section === BOARDS) {
        items = items.filter(i => i.type === 'board')
      } else {
        items = items
          .filter(i => i.type === 'folder')
          .filter(i => !i.parent || !items.find(({ id }) => id === i.parent))
      }
    }

    if (filter === VIEWED)
      return items
        .filter(i => i.dateLastViewed)
        .sort((a, b) => (b.dateLastViewed || 0) - (a.dateLastViewed || 0))

    if (filter === CREATED) return items.sort(dateCreated)

    if (filter === NAME)
      return items.sort((a, b) => lower(a).localeCompare(lower(b)))

    if (filter === UNFILED) return items.filter(i => !i.parent)

    if (filter === MINE) return items.filter(i => i.isOwner).sort(dateCreated)

    if (filter === SHARED)
      return items
        .filter(i => i.dateAccepted)
        .sort((a, b) => b.dateAccepted - a.dateAccepted)

    return items
  }
)

export const sidebarFoldersSelector = createSelector(
  [accessListSelector, sidebarFolderSelector],
  (accessList, folder) => {
    if (!isLoaded(accessList) || !accessList || !folder) return
    const folders = []
    let folderId = folder.id
    while (
      folderId &&
      folderId in accessList &&
      accessList[folderId].accessLevel
    ) {
      folders.unshift(accessList[folderId])
      folderId = accessList[folderId].parent
    }

    return folders
  }
)
